import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const parseEmails = (s) => (s || "").split(/[,;\s]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

export const EmailQuoteDialog = ({ quote, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [altro, setAltro] = useState("");
  const [cc, setCc] = useState("");
  const [oggetto, setOggetto] = useState("");
  const [messaggio, setMessaggio] = useState("");

  useEffect(() => {
    if (!open || !quote) return;
    setLoading(true);
    api.get(`/crm-quotes/${quote.id}/email-candidates`)
      .then((r) => {
        const cands = r.data.candidates || [];
        setCandidates(cands);
        setSelected(Object.fromEntries(cands.map((c, i) => [c.email, i === 0])));
        setOggetto(r.data.default_subject || "");
        setMessaggio(r.data.default_body || "");
        setAltro(""); setCc("");
      })
      .catch(() => toast.error("Impossibile caricare le email del cliente"))
      .finally(() => setLoading(false));
  }, [open, quote]);

  const send = async () => {
    const to = [...candidates.filter((c) => selected[c.email]).map((c) => c.email), ...parseEmails(altro)];
    const uniqTo = [...new Set(to.map((e) => e.trim()).filter(Boolean))];
    if (uniqTo.length === 0) { toast.error("Seleziona almeno un destinatario"); return; }
    setSending(true);
    try {
      const r = await api.post(`/crm-quotes/${quote.id}/invia-email`, {
        to: uniqTo, cc: parseEmails(cc), oggetto, messaggio,
      });
      toast.success(`Preventivo inviato a ${r.data.to.length} destinatario/i`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="quote-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Invia preventivo {quote?.numero}</DialogTitle>
          <DialogDescription>Il PDF della proposta viene allegato automaticamente. Una copia arriva anche a te in CC.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Destinatari cliente</Label>
              {candidates.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground" data-testid="quote-email-no-candidates">Nessuna email in anagrafica: usa il campo "Altro indirizzo".</p>
              ) : (
                <div className="mt-1.5 space-y-1.5">
                  {candidates.map((c) => (
                    <label key={c.email} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 text-sm" data-testid={`quote-email-cand-${c.email}`}>
                      <Checkbox checked={!!selected[c.email]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.email]: !!v }))} data-testid={`quote-email-cb-${c.email}`} />
                      <span className="font-medium">{c.email}</span>
                      {c.label && c.label !== c.email && <span className="text-muted-foreground">· {c.label}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="q-altro" className="text-xs font-semibold uppercase text-muted-foreground">Altro indirizzo (opzionale)</Label>
              <Input id="q-altro" value={altro} onChange={(e) => setAltro(e.target.value)} placeholder="email1@dominio.it, email2@dominio.it" className="mt-1" data-testid="quote-email-altro" />
            </div>
            <div>
              <Label htmlFor="q-cc" className="text-xs font-semibold uppercase text-muted-foreground">CC (opzionale)</Label>
              <Input id="q-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia.conoscenza@dominio.it" className="mt-1" data-testid="quote-email-cc" />
            </div>
            <div>
              <Label htmlFor="q-ogg" className="text-xs font-semibold uppercase text-muted-foreground">Oggetto</Label>
              <Input id="q-ogg" value={oggetto} onChange={(e) => setOggetto(e.target.value)} className="mt-1" data-testid="quote-email-oggetto" />
            </div>
            <div>
              <Label htmlFor="q-msg" className="text-xs font-semibold uppercase text-muted-foreground">Messaggio</Label>
              <Textarea id="q-msg" value={messaggio} onChange={(e) => setMessaggio(e.target.value)} rows={7} className="mt-1" data-testid="quote-email-messaggio" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="quote-email-cancel">Annulla</Button>
          <Button onClick={send} disabled={sending || loading} data-testid="quote-email-send">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
